import React from 'react';
import PropTypes from 'prop-types';

const Endpoint = ({ 
  method, 
  path, 
  description, 
  queryParams = [], 
  pathParams = [], 
  requestBody,
  response,
  exampleRequest,
  exampleResponse 
}) => {
  return (
    <div className="lndad-endpoint">
      <h3>
        <span className="lndad-method">{method}</span>
        <span className="lndad-path">{path}</span>
      </h3>

      <div className="lndad-endpoint-body">
        <div className="lndad-endpoint-left">
          <p style={{ marginBottom: '24px', color: 'var(--text-primary)', lineHeight: '1.6' }}>
            {description}
          </p>

          {/* Path Parameters */}
          {pathParams.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h4>Path Parameters</h4>
              <table className="lndad-table">
                <thead>
                  <tr>
                    <th className="lndad-th">Parameter</th>
                    <th className="lndad-th">Type</th>
                    <th className="lndad-th">Required</th>
                    <th className="lndad-th">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {pathParams.map((param) => (
                    <tr key={param.name}>
                      <td className="lndad-td">{param.name}</td>
                      <td className="lndad-td">{param.type}</td>
                      <td className="lndad-td">{param.required ? 'Yes' : 'No'}</td>
                      <td className="lndad-td">{param.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Query Parameters */}
          {queryParams.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h4>Query Parameters</h4>
              <table className="lndad-table">
                <thead>
                  <tr>
                    <th className="lndad-th">Parameter</th>
                    <th className="lndad-th">Type</th>
                    <th className="lndad-th">Required</th>
                    <th className="lndad-th">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {queryParams.map((param) => (
                    <tr key={param.name}>
                      <td className="lndad-td">{param.name}</td>
                      <td className="lndad-td">{param.type}</td>
                      <td className="lndad-td">{param.required ? 'Yes' : 'No'}</td>
                      <td className="lndad-td">{param.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Request Body */}
          {requestBody?.schema && (
            <div style={{ marginBottom: '24px' }}>
              <h4>Request Body</h4>
              <div className="lndad-code-block">
                <pre>
                  <code>{JSON.stringify(requestBody.schema, null, 2)}</code>
                </pre>
                <button
                  className="lndad-copy-btn"
                  onClick={() =>
                    navigator.clipboard.writeText(
                      JSON.stringify(requestBody.schema, null, 2)
                    )
                  }
                >
                  Copy
                </button>
              </div>
            </div>
          )}

          {/* Response */}
          <div style={{ marginBottom: '24px' }}>
            <h4>Response</h4>
            <div>
              <strong>Status Code:</strong>{' '}
              <span className="lndad-status-badge">{response.status}</span>
            </div>
            <div>
              <strong>Content:</strong> {response.content}
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="lndad-endpoint-right">
          <div className="lndad-example-section">
            <h5>Example Request</h5>
            <div className="lndad-code-block">
              <pre>
                <code>{exampleRequest}</code>
              </pre>
              <button
                className="lndad-copy-btn"
                onClick={() => navigator.clipboard.writeText(exampleRequest)}
              >
                Copy
              </button>
            </div>
          </div>

          <div className="lndad-example-section">
            <h5>Example Response</h5>
            <div className="lndad-code-block">
              <pre>
                <code>{JSON.stringify(exampleResponse, null, 2)}</code>
              </pre>
              <button
                className="lndad-copy-btn"
                onClick={() =>
                  navigator.clipboard.writeText(
                    JSON.stringify(exampleResponse, null, 2)
                  )
                }
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* PropTypes */

Endpoint.propTypes = {
  method: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,

  queryParams: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      required: PropTypes.bool,
      description: PropTypes.string
    })
  ),

  pathParams: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      required: PropTypes.bool,
      description: PropTypes.string
    })
  ),

  requestBody: PropTypes.shape({
    schema: PropTypes.object
  }),

  response: PropTypes.shape({
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    content: PropTypes.string.isRequired
  }).isRequired,

  exampleRequest: PropTypes.string.isRequired,
  exampleResponse: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired
};

export default Endpoint;
